import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Alert,
	Anchor,
	Badge,
	Box,
	Button,
	Checkbox,
	CloseButton,
	Group,
	Loader,
	Modal,
	Paper,
	ScrollArea,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
} from '@mantine/core'
import {
	addStaffPosition,
	deleteStaffPosition,
	getExperiencePositions,
	getStationOptions,
} from '../services/actions/experienceActions'
import { getOrgUsers } from '../services/actions/orgActions'
import { toast } from '../services/utils/toast'
import StaffAssignmentModal from '../components/StaffAssignmentModal'

const POSITION_LEVEL_LABELS = { owner: 'Owner', staff: 'Staff' }

const ManageStaff = ({ experienceSlug, orgSlug }) => {
	const [users, setUsers] = useState([])
	const [positions, setPositions] = useState([])
	const [stationOptions, setStationOptions] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState('')
	const [search, setSearch] = useState('')
	const [selectedUserIds, setSelectedUserIds] = useState([])
	const [assignmentUserIds, setAssignmentUserIds] = useState([])
	const [positionToRemove, setPositionToRemove] = useState(null)
	const [removing, setRemoving] = useState(false)
	const [removalError, setRemovalError] = useState('')
	const currentOrg = useSelector((state) => state.user.currentOrg)
	const orgRole = useSelector((state) => state.org.orgRole)
	const userType = useSelector((state) => state.user.authUser?.userType)
	const isAdmin = orgRole === 'admin' || userType === 'superAdmin'
	const organizationName = currentOrg?.orgName || 'your organization'

	const positionByUserId = new Map(positions.map((p) => [p.userId, p]))
	const staffUsers = users
		.filter((user) => positionByUserId.has(user.id))
		.sort((a, b) => {
			const ownerOrder =
				Number(positionByUserId.get(b.id).level === 'owner') -
				Number(positionByUserId.get(a.id).level === 'owner')
			return ownerOrder || (a.name || a.email || '').localeCompare(
				b.name || b.email || '', undefined, { sensitivity: 'base' },
			)
		})
	const availableUsers = users.filter(
		(user) => user.isActive !== false && !positionByUserId.has(user.id),
	)
	const query = search.trim().toLowerCase()
	const searchableUsers = query
		? [...availableUsers, ...staffUsers]
		: availableUsers
	const filteredUsers = searchableUsers.filter(
		(user) =>
			user.name?.toLowerCase().includes(query) ||
			user.email?.toLowerCase().includes(query),
	)
	const selectedUsers = availableUsers.filter((user) =>
		selectedUserIds.includes(user.id),
	)
	const assignmentUsers = availableUsers.filter((user) =>
		assignmentUserIds.includes(user.id),
	)
	const getStationName = (position) =>
		position.stationName || stationOptions.find((station) => station.id === position.station)?.name

	useEffect(() => {
		let cancelled = false
		const fetchData = async () => {
			try {
				const [orgUsers, staffPositions, stations] = await Promise.all([
					getOrgUsers(orgSlug),
					getExperiencePositions(experienceSlug, orgSlug),
					getStationOptions(experienceSlug, orgSlug),
				])
				if (cancelled) return
				setUsers(orgUsers)
				setPositions(staffPositions)
				setStationOptions(stations)
			} catch (err) {
				if (!cancelled) {
					setLoadError('Failed to load staff data. Please reload the page.')
				}
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		fetchData()
		return () => {
			cancelled = true
		}
	}, [experienceSlug, orgSlug])

	const toggleUser = (userId) => {
		setSelectedUserIds((current) =>
			current.includes(userId)
				? current.filter((id) => id !== userId)
				: [...current, userId],
		)
	}

	const handleAddStaff = async (assignment) => {
		const results = await Promise.allSettled(
			assignmentUsers.map((user) =>
				addStaffPosition({
					orgSlug,
					experienceSlug,
					userId: user.id,
					...assignment,
				}),
			),
		)
		const addedPositions = []
		const failedUsers = []
		results.forEach((result, index) => {
			if (result.status === 'fulfilled') addedPositions.push(result.value)
			else failedUsers.push(assignmentUsers[index])
		})

		const addedUserIds = addedPositions.map((position) => position.userId)
		setPositions((current) => [...current, ...addedPositions])
		setSelectedUserIds((current) =>
			current.filter((id) => !addedUserIds.includes(id)),
		)
		setAssignmentUserIds(failedUsers.map((user) => user.id))

		if (addedPositions.length > 0) {
			toast.success({
				title: 'Staff added',
				message: `${addedPositions.length} ${addedPositions.length === 1 ? 'user' : 'users'} added to staff.`,
			})
		}
		if (failedUsers.length > 0) {
			throw new Error(
				`Could not add ${failedUsers.map((user) => user.name || user.email).join(', ')}. Retry to add the remaining ${failedUsers.length === 1 ? 'user' : 'users'}.`,
			)
		}
	}

	const handleRemoveStaff = async () => {
		setRemoving(true)
		setRemovalError('')
		try {
			await deleteStaffPosition(experienceSlug, orgSlug, positionToRemove.id)
			setPositions((current) =>
				current.filter((position) => position.id !== positionToRemove.id),
			)
			setPositionToRemove(null)
		} catch (err) {
			setRemovalError('Failed to remove staff. Please try again.')
		} finally {
			setRemoving(false)
		}
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='lg'>
				Manage Staff
			</Title>
			{loading ? (
				<Group position='center' py='xl'>
					<Loader aria-label='Loading staff' />
				</Group>
			) : loadError ? (
				<Alert color='red' title='Staff unavailable'>
					{loadError}
				</Alert>
			) : (
				<Stack spacing='xl'>
					<Paper withBorder p='md' radius='md'>
						<Stack spacing='md'>
							<Box>
								<Title order={4}>Add staff</Title>
								<Text size='sm' color='dimmed' mt={4}>
									Search users, then add one or select multiple to give them
									the same position and station.
								</Text>
								<TextInput
									mt='md'
									aria-label='Search users'
									placeholder='Search by name or email'
									value={search}
									onChange={(event) => setSearch(event.target.value)}
								/>
							</Box>

							{selectedUsers.length > 0 && (
								<Paper withBorder p='sm' radius='sm'>
									<Group position='apart' mb='sm'>
										<Text size='sm' weight={500} aria-live='polite'>
											{selectedUsers.length} selected
										</Text>
										<Group spacing='xs'>
											<Button
												variant='subtle'
												color='gray'
												size='xs'
												onClick={() => setSelectedUserIds([])}
											>
												Clear selection
											</Button>
											<Button
												size='sm'
												onClick={() =>
													setAssignmentUserIds(
														selectedUsers.map((user) => user.id),
													)
												}
											>
												Add selected ({selectedUsers.length})
											</Button>
										</Group>
									</Group>
									<Group spacing='xs'>
										{selectedUsers.map((user) => (
											<Badge
												key={user.id}
												size='lg'
												pr={3}
												styles={{ inner: { textTransform: 'none' } }}
												rightSection={
													<CloseButton
														size='xs'
														variant='transparent'
														aria-label={`Deselect ${user.name || user.email}`}
														onClick={() => toggleUser(user.id)}
													/>
												}
											>
												{user.name || user.email}
											</Badge>
										))}
									</Group>
								</Paper>
							)}

							{filteredUsers.length > 0 ? (
								<ScrollArea.Autosize mah={360} type='auto'>
									<Table highlightOnHover aria-label='User results'>
										<thead>
											<tr>
												<th style={{ width: 60 }}>Select</th>
												<th>User</th>
												<th style={{ width: 80 }}>Action</th>
											</tr>
										</thead>
										<tbody>
											{filteredUsers.map((user) => {
												const position = positionByUserId.get(user.id)
												return (
													<tr key={user.id}>
														<td>
															{!position && (
																<Checkbox
																	aria-label={`Select ${user.name || user.email}`}
																	checked={selectedUserIds.includes(user.id)}
																	onChange={() => toggleUser(user.id)}
																/>
															)}
														</td>
														<td>
															<Text size='sm' weight={500}>
																{user.name || '—'}
															</Text>
															<Text
																size='xs'
																color='dimmed'
																sx={{ overflowWrap: 'anywhere' }}
															>
																{user.email}
															</Text>
															{position && (
																<Text size='xs' color='dimmed'>
																	{[
																		position.positionName || POSITION_LEVEL_LABELS[position.level],
																		getStationName(position),
																	].filter(Boolean).join(' · ')}
																</Text>
															)}
														</td>
														<td>
															{position ? (
																<Badge color='gray' variant='light'>Assigned</Badge>
															) : (
																<Button
																	size='xs'
																	variant='light'
																	aria-label={`Add ${user.name || user.email}`}
																	onClick={() => setAssignmentUserIds([user.id])}
																>
																	Add
																</Button>
															)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</Table>
								</ScrollArea.Autosize>
							) : (
								<Text size='sm' color='dimmed' py='sm'>
									{query
										? 'No users match your search.'
										: 'All available users have been assigned.'}
								</Text>
							)}
							<Text size='sm' color='dimmed'>
								Don't see who you are looking for?{' '}
								{isAdmin ? (
									<Anchor
										component={Link}
										to={`/${orgSlug}/admin/users`}
										size='sm'
									>
										Add a user to {organizationName}'s LiveHQ account.
									</Anchor>
								) : (
									<>Ask a {organizationName} admin to add the user to LiveHQ.</>
								)}
							</Text>
						</Stack>
					</Paper>

					<Box>
						<Title order={4} mb='sm'>
							Assigned staff ({staffUsers.length})
						</Title>
						{staffUsers.length > 0 ? (
							<Paper withBorder p='md' radius='md'>
								<ScrollArea type='auto'>
									<Table
										striped
										highlightOnHover
										aria-label='Assigned staff'
										miw={650}
									>
										<thead>
											<tr>
												<th>Name</th>
												<th>Email</th>
												<th>Level</th>
												<th>Position</th>
												<th>Station</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{staffUsers.map((user) => {
												const position = positionByUserId.get(user.id)
												return (
													<tr key={user.id}>
														<td>{user.name || '—'}</td>
														<td>{user.email}</td>
														<td>
															{POSITION_LEVEL_LABELS[position.level] || '—'}
														</td>
														<td>{position.positionName || '—'}</td>
														<td>{getStationName(position) || '—'}</td>
														<td>
															<Group spacing='xs' noWrap>
																<Button
																	component={Link}
																	to={`/${orgSlug}/experiences/${experienceSlug}/manage/staff/${user.id}/edit`}
																	size='xs'
																	variant='light'
																	aria-label={`Edit ${user.name || user.email}`}
																>
																	Edit
																</Button>
																<Button
																	size='xs'
																	variant='subtle'
																	color='red'
																	aria-label={`Remove ${user.name || user.email}`}
																	onClick={() => {
																		setRemovalError('')
																		setPositionToRemove({
																			id: position.id,
																			name: user.name || user.email,
																		})
																	}}
																>
																	Remove
																</Button>
															</Group>
														</td>
													</tr>
												)
											})}
										</tbody>
									</Table>
								</ScrollArea>
							</Paper>
						) : (
							<Text size='sm' color='dimmed'>
								No staff assigned yet.
							</Text>
						)}
					</Box>
				</Stack>
			)}

			{assignmentUsers.length > 0 && (
				<StaffAssignmentModal
					users={assignmentUsers}
					stationOptions={stationOptions}
					onClose={() => setAssignmentUserIds([])}
					onSave={handleAddStaff}
				/>
			)}
			<Modal
				opened={Boolean(positionToRemove)}
				onClose={() => {
					if (!removing) setPositionToRemove(null)
				}}
				title='Remove staff'
				centered
				closeOnClickOutside={!removing}
				closeOnEscape={!removing}
				withCloseButton={!removing}
			>
				{removalError && (
					<Alert color='red' mb='md'>
						{removalError}
					</Alert>
				)}
				<Text mb='md'>
					Remove <strong>{positionToRemove?.name}</strong> from this
					experience's staff?
				</Text>
				<Group position='right'>
					<Button
						variant='default'
						disabled={removing}
						onClick={() => setPositionToRemove(null)}
					>
						Cancel
					</Button>
					<Button color='red' loading={removing} onClick={handleRemoveStaff}>
						Remove staff
					</Button>
				</Group>
			</Modal>
		</Box>
	)
}

const ManageStaffPage = () => {
	const { experienceSlug, orgSlug } = useParams()
	// Reset selections and pending dialogs when switching experiences.
	return (
		<ManageStaff
			key={`${orgSlug}/${experienceSlug}`}
			experienceSlug={experienceSlug}
			orgSlug={orgSlug}
		/>
	)
}

export default ManageStaffPage
